import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useVoiceAgentConversation } from './useVoiceAgentConversation';
import { useAudioSessionControls } from './useAudioSessionControls';
import { useTextChatSession } from './useTextChatSession';
import { useVoiceTranscript } from './useVoiceTranscript';
import type { Agent, AgentConfig } from '@/types/agent/agent';
import type { QuickTestMode } from '@/types/agent/quick-test';
import type { TranscriptMessage } from '@/pages/agent-interfaces/live-agent/components/TranscriptPanel';

const noop = () => undefined;

export const useQuickTestConversation = (genie: Agent, onClose: () => void) => {
  const [mode, setMode] = useState<QuickTestMode>('chat');
  const [messages, setMessages] = useState<TranscriptMessage[]>([]);
  const [isEnding, setIsEnding] = useState(false);
  const busyRef = useRef(false);
  const conversationStartTimeRef = useRef<number | null>(null);
  const voice = useVoiceAgentConversation(genie.elevenlabs_id, {
    agentData: genie,
    welcomeMessage: 'Ready to test',
    errorMessage: mode === 'chat'
      ? 'Could not start chat. Please try again.'
      : 'Could not start voice. Please check your microphone permissions and try again.',
    isQuickTest: true,
  });
  const audio = useAudioSessionControls({ voiceAgent: voice });
  const endConversation = useCallback(async () => {
    await voice.endConversation();
  }, [voice]);
  const chat = useTextChatSession({
    voiceAgent: voice,
    audio,
    attachments: [],
    removeAttachment: noop,
    stageImages: noop,
    endConversationAfterUploads: endConversation,
    setMessages,
    setShowTranscript: noop,
    setConversationStartTime: (time) => { conversationStartTimeRef.current = time; },
    clientEvents: (genie.settings as AgentConfig | undefined)?.conversation_config?.conversation?.client_events,
    muteOnTextStart: true,
  });
  useVoiceTranscript({
    voiceAgent: voice,
    setMessages,
    optimisticUserMessagesRef: chat.optimisticUserMessagesRef,
    conversationStartTimeRef,
  });

  // Await the current session before switching transport or closing the sheet.
  const finishSession = async (next?: QuickTestMode) => {
    if (busyRef.current || voice.isConnecting) return;
    busyRef.current = true;
    setIsEnding(true);
    try {
      await endConversation();
      voice.setOverrides(undefined);
      if (next) {
        chat.setIsTextOnlyMode(false);
        chat.setStartedFromText(false);
        chat.optimisticUserMessagesRef.current = [];
        setMessages([]);
        setMode(next);
      } else {
        onClose();
      }
    } catch {
      toast.error('Could not end the conversation. Please try again.');
    } finally {
      busyRef.current = false;
      setIsEnding(false);
    }
  };

  return {
    mode,
    messages,
    voice,
    audio,
    chat,
    isBusy: voice.isConnecting || isEnding,
    changeMode: (next: string) => {
      if ((next === 'chat' || next === 'voice') && next !== mode) void finishSession(next);
    },
    close: () => { void finishSession(); },
    endChat: async () => {
      if (busyRef.current || voice.isConnecting) return;
      busyRef.current = true;
      setIsEnding(true);
      try {
        await chat.endTextChat();
      } finally {
        busyRef.current = false;
        setIsEnding(false);
      }
    },
  };
};
